import React, { useContext, createContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'react-apollo';
import { BasePage, Helmet, StaticContext, SurveyTheme, FourOhFourErrorPage } from '@sm/webassets';
import { Container } from '@sm/wds-react';
import { t } from '@sm/intl';
import { Header, EnterpriseThankYouBody, EnterpriseThankYouFooter, EnterpriseThankYouLayout } from './components';
import { getDefaultEndPageSettings } from './EnterpriseThankYou.graphql';
import COPY from './copy';
import './enterprise-thank-you.scss';

export const EnterpriseThankYouContext = createContext({});
export const PageVariants = {
  standard: Symbol('standard'),
  premiumStandard: Symbol('premium_standard'),
  premiumCustom: Symbol('premium_custom'),
};

const determinePageVariant = (isStandard, isPremiumCustomizable) => {
  switch (true) {
    case isStandard === true:
      return PageVariants.standard;
    case isPremiumCustomizable === true:
      return PageVariants.premiumCustom;
    default:
      return PageVariants.premiumStandard;
  }
};

const EnterpriseThankYou = () => {
  const { pageOptions } = useContext(StaticContext);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const smParam = searchParams.get('sm');
  const surveyId = pageOptions?.surveyId;
  const hasStandard = pageOptions?.hasStandard;

  // Pass the encrypted parameter back to lo-graph use case for basic authentication
  const thankYouHeaders = {
    'X-SM-PARAM': smParam,
  };
  const {
    loading: loadingThankYou,
    error: errorThankYou,
    data: endPageSettings,
  } = useQuery(getDefaultEndPageSettings, {
    variables: {
      smParam,
    },
    context: {
      targetPath: '/lo-graphql/enterpriseThankYou',
      headers: thankYouHeaders,
    },
  });

  if (loadingThankYou) {
    return null;
  }

  if (errorThankYou || endPageSettings === null) {
    return <FourOhFourErrorPage />;
  }

  if (endPageSettings) {
    const customEndPageIsEnabled = endPageSettings?.defaultEndPageSettings?.isEnabled; // Used to distinguish between the "custom" and "standard" premium pages, which is different from standard/premium vars taken from smParam
    const headerLogoUrl = endPageSettings?.defaultEndPageSettings?.logo?.url;
    const headerText = endPageSettings?.defaultEndPageSettings?.thankYouText;
    const bodyImageUrl = endPageSettings?.defaultEndPageSettings?.image?.url;
    const bodyText = endPageSettings?.defaultEndPageSettings?.bodyText;
    const bodyImageRedirectUrl = endPageSettings?.defaultEndPageSettings?.imageRedirectUrl;
    const pageVariant = determinePageVariant(hasStandard, customEndPageIsEnabled);

    return (
      <BasePage
        actionFlow="EnterpriseThankYou"
        pageId="EnterpriseThankYou"
        legacyWeb="respweb"
        includeHeader={false}
        includeFooter={false}
      >
        <Helmet>
          <base target="_top" href="/" />
          <title>{t(COPY.THANK_YOU)}</title>
          <meta name="description" content={t(COPY.THANK_YOU)} />
        </Helmet>
        <SurveyTheme surveyId={surveyId} targetEndPoint="enterpriseThankYou" headers={thankYouHeaders}>
          <EnterpriseThankYouContext.Provider value={{ pageVariant }}>
            <EnterpriseThankYouLayout>
              <Container
                className="sm-enterprise-thank-you__outline"
                flexJustify="center"
                py={8}
                data-testid="EnterpriseThankYou__OutlineContainer"
              >
                <Container className="sm-enterprise-thank-you__content-outline">
                  <Header headerLogoUrl={headerLogoUrl} headerText={headerText} />
                  <EnterpriseThankYouBody
                    bodyImageUrl={bodyImageUrl}
                    bodyText={bodyText}
                    bodyImageRedirectUrl={bodyImageRedirectUrl}
                    customEndPageIsEnabled={customEndPageIsEnabled}
                  />
                </Container>
                <EnterpriseThankYouFooter />
              </Container>
            </EnterpriseThankYouLayout>
          </EnterpriseThankYouContext.Provider>
        </SurveyTheme>
      </BasePage>
    );
  }
  return null;
};

export default EnterpriseThankYou;
